import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import parse from 'html-react-parser';

import { requestSize } from "../../store/requests/thunks/requestSize";
import { requestSizeUpdate } from "../../store/requests/thunks/requestSizeUpdate";
import { setRequestsAsIdle } from "../../store/requests/requestsSlice";

import { alert } from "../../helpers/alert";

import SkeletonView from "../Skeleton/SkeletonView";

import Preview from "../../components/Preview/Preview";
import PreviewBar from "../../components/PreviewBar/PreviewBar";
import OptionList from "../../components/OptionList/OptionList";

import Grid from "@mui/material/Grid";

import NavigateButton from "../../designComponents/NavigateButton/NavigateButton";
import { _VerticalSeparator as VerticalSeparator } from "../../designComponents/VerticalSeparator/style";

import {
  _SizeViewContainer as SizeViewContainer,
  _LayoutSectionSizeView as LayoutSection,
} from "./SizeViewStyle";

const SizeView = () => {
  const dispatch = useDispatch();

  const init = useSelector((state) => state.init);
  const Uterum = useSelector((state) => state.Uterum);
  const Options = useSelector((state) => state.Options);
  const requests = useSelector((state) => state.requests);

  const { assetBaseUrl, baseRoute, alias } = init;

  const [_1000msSinceUpdateReqInit, set1000msSinceUpdateReqInit] =
    useState(false);

  const theme = useTheme();
  let navigate = useNavigate();

  const previewRef = useRef(null);

  // Init when view did mount --- Cleanup when view unmounts
  useEffect(() => {
    window.scrollTo(0, 0);

    dispatch(requestSize());

    return () => {
      dispatch(setRequestsAsIdle(["requestSize", "requestSizeUpdate"]));
    };
  }, [dispatch]);

  // Handle delay on pending update
  useEffect(() => {
    let timeout = null;

    if (
      requests.status.requestSizeUpdate === "pending" &&
      _1000msSinceUpdateReqInit === false
    ) {
      timeout = setTimeout(() => {
        set1000msSinceUpdateReqInit(true);
      }, 1000);
    } else if (
      requests.status.requestSizeUpdate !== "pending" &&
      _1000msSinceUpdateReqInit
    ) {
      set1000msSinceUpdateReqInit(false);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [requests.status.requestSizeUpdate]);

  // Handle rejected
  // (TODO 3062: Improve UX / error handling when requestSizeUpdate is rejected?)
  useEffect(() => {
    if (
      requests.status.requestSize === "rejected" ||
      requests.status.requestSizeUpdate === "rejected"
    ) {
      alert({
        title: alias.bfcGenericErrorMessage,
        text: alias.bfcContactUs,
        icon: "error",
        confirmButtonColor: theme.primary,
        onConfirm: () => {
          navigate(`../${baseRoute}/step1`, { replace: true });
        },
      });
    }
  }, [requests.status.requestSize, requests.status.requestSizeUpdate]);

  const renderMode = (() => {
    if (
      requests.status.requestSize === "rejected" ||
      requests.status.requestSizeUpdate === "rejected"
    ) {
      return "none";
    }

    if (
      requests.status.requestSize === "idle" ||
      requests.status.requestSize === "pending" ||
      (requests.status.requestSizeUpdate === "pending" &&
        _1000msSinceUpdateReqInit)
    ) {
      return "loader";
    }

    return "default";
  })();

  if (renderMode === "loader") {
    return <SkeletonView forView="SizeView" />;
  }

  if (renderMode === "default") {
    return (
      <SizeViewContainer>
        <LayoutSection sticky ref={previewRef}>
          <Preview renderDimensions={false} dataId="Preview" />
        </LayoutSection>

        <PreviewBar previewRef={previewRef} dataId="PreviewBar" />

        <VerticalSeparator />

        {/* TODO setup the correct layout again */}
        <LayoutSection>
          <Grid container spacing="15px">

            {/* -- FrameWidth -- */}
            <Grid item xs={12} md={6}>
              <OptionList
                renderAs="select"
                hideBorder={true}
                name="FrameWidth"
                label={alias.bfcChooseWidth}
                tooltipContent={alias.tooltip_width && parse(alias.tooltip_width)}
                options={Options.Widths.map((width) => ({
                  value: width.Value,
                  label: `${width.Title} mm`,
                }))}
                activeOptionValueFromParent={Uterum.FrameWidth}
                onChange={(value) => {
                  dispatch(
                    requestSizeUpdate({
                      uterumProperties: {
                        FrameWidth: value,
                        PaintArticleErpId: "", // always reset here
                        PaintPanelArticleErpId: "", // always reset here
                      },
                    })
                  );
                }}
                />
              </Grid>

            {/* -- FrameDepth -- */}
            <Grid item xs={12} md={6}>
              <OptionList
                renderAs="select"
                name="FrameDepth"
                hideBorder={true}
                label={alias.bfcChooseHeight}
                tooltipContent={alias.tooltip_depth && parse(alias.tooltip_depth)}
                /*
                  Note: 'Options.Heights' below due to wrong name in CGE API,
                  this should actually be 'Options.Depthts'
                */
                options={Options.Heights.map((depth) => ({
                  value: depth.Value,
                  label: `${depth.Title} mm`,
                }))}
                activeOptionValueFromParent={Uterum.FrameDepth}
                onChange={(value) => {
                  dispatch(
                    requestSizeUpdate({
                      uterumProperties: {
                        FrameDepth: value,
                        PaintArticleErpId: "", // always reset here
                        PaintPanelArticleErpId: "", // always reset here
                      },
                    })
                  );
                }}
              />
            </Grid>

            {/* -- FrameAddonWidth -- */}
            {Options.AddonWidths && Options.AddonWidths.length > 0 && (
              <Grid item xs={12} md={6}>
                <OptionList
                  hideBorder={true}
                  renderAs="select"
                  name="FrameAddonWidth"
                  label={alias.bfcChooseAddonWidth}
                  tooltipContent={alias.tooltip_addon_width && parse(alias.tooltip_addon_width)}
                  options={Options.AddonWidths.map((addonWidth) => ({
                    value: addonWidth.Value,
                    label: `${addonWidth.Title} mm`,
                  }))}
                  activeOptionValueFromParent={Uterum.FrameAddonWidth}
                  onChange={(value) => {
                    dispatch(
                      requestSizeUpdate({
                        uterumProperties: {
                          FrameAddonWidth: value,
                          PaintArticleErpId: "", // always reset here
                          PaintPanelArticleErpId: "", // always reset here
                        },
                      })
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>

          {/* -- Corners -- */}
          {Options.Corners && Options.Corners.length > 0 && (   
              <OptionList
                hideBorder={true}
                renderAs="thumbnails"
                name="Corners"
                label={alias.bfcWhichCorner}
                tooltipContent={alias.tooltip_corners && parse(alias.tooltip_corners)}
                options={Options.Corners.map((corner) => ({
                  value: corner.Value,
                  imgSrc: `${assetBaseUrl}/store/uterumsguide2016/corner/${corner.Value}.png`,
                  imgAlt: corner.Title,
                }))}
                activeOptionValueFromParent={Uterum.Corners}
                onChange={(value) => {
                  dispatch(
                    requestSizeUpdate({
                      uterumProperties: {
                        Corners: value,
                        PaintArticleErpId: "", // always reset here
                        PaintPanelArticleErpId: "", // always reset here
                      },
                    })
                  );
                }}
              />
          )}

          <NavigateButton
            handleClick={() => navigate(`../${baseRoute}/step4`)}
            dataId={"NextStepButton"}
            text={alias.bfcNextStep}
            widthOnDesktop="45%"
          />
          
        </LayoutSection>
      </SizeViewContainer>
    );
  }

  /*
  If status for request(s) is 'rejected' --> renderMode is "none"
  --> error handling for UI is done in useEffect
  */
  return null;
};

export default SizeView;
